/**
 * Browser Debugging Utilities for HotKeys System
 *
 * Provides runtime inspection and control of the HotKeys feature.
 * Available in browser console via window.__HOTKEYS_DEBUG__
 *
 * Usage:
 * - __HOTKEYS_DEBUG__.isEnabled() → Check if feature is enabled
 * - __HOTKEYS_DEBUG__.getPermissions() → View current module permissions
 * - __HOTKEYS_DEBUG__.forceEnable() → Override and enable (reloads page)
 * - __HOTKEYS_DEBUG__.forceDisable() → Override and disable (reloads page)
 * - __HOTKEYS_DEBUG__.listRegistered() → Show all registered hotkeys
 * - __HOTKEYS_DEBUG__.getConfig() → View all layer configurations
 * - __HOTKEYS_DEBUG__.permissions.override({...}) → Override permissions at runtime
 * - __HOTKEYS_DEBUG__.permissions.grantAll() → Grant all permissions
 * - __HOTKEYS_DEBUG__.permissions.denyAll() → Deny all permissions
 * - __HOTKEYS_DEBUG__.permissions.reset() → Reset to metadata defaults
 */
import { HOTKEYS_FEATURE_ENABLED, getEnvVar, isDevelopmentMode } from "../constants";

/**
 * Runtime permission overrides storage key.
 */
const PERMISSION_OVERRIDE_KEY = "capitalrx-hotkeys-permission-override";

/**
 * Interface for permission override configuration.
 */
interface PermissionOverride {
  /** Override module-level registration permission. */
  canRegister?: boolean;
  /** Override global scope permission. */
  canUseGlobalScope?: boolean;
  /** Override allowed scopes. */
  allowedScopes?: string[];
  /** Override max registrations. */
  maxRegistrations?: number;
  /** Override strict mode. */
  strictMode?: boolean;
  /** Per-scope overrides. */
  scopeConfig?: {
    [scopeName: string]: {
      enabled?: boolean;
      maxRegistrations?: number;
    };
  };
  /** When the override was set (for debugging). */
  timestamp?: number;
  /** Reason for override (for debugging). */
  reason?: string;
}

/**
 * Get current permission overrides from storage.
 */
function getPermissionOverrides(): PermissionOverride | null {
  if (typeof window === "undefined") return null;

  // Check sessionStorage first (temporary), then localStorage (persistent)
  const sessionOverride = sessionStorage.getItem(PERMISSION_OVERRIDE_KEY);
  if (sessionOverride) {
    try {
      return JSON.parse(sessionOverride);
    } catch (error) {
      console.warn("[HotKeys Debug] Failed to parse permission override from session storage:", error);
      // Clear corrupted data
      sessionStorage.removeItem(PERMISSION_OVERRIDE_KEY);
    }
  }

  const localOverride = localStorage.getItem(PERMISSION_OVERRIDE_KEY);
  if (localOverride) {
    try {
      return JSON.parse(localOverride);
    } catch (error) {
      console.warn("[HotKeys Debug] Failed to parse permission override from local storage:", error);
      // Clear corrupted data
      localStorage.removeItem(PERMISSION_OVERRIDE_KEY);
    }
  }

  // Also check window global for immediate runtime overrides
  return (window as any).__HOTKEYS_PERMISSION_OVERRIDE__ || null;
}

/**
 * Set permission overrides.
 * @param override The override configuration.
 * @param persist If true, saves to localStorage. If false, uses sessionStorage (cleared on tab close).
 */
function setPermissionOverrides(override: PermissionOverride, persist = false): void {
  if (typeof window === "undefined") return;

  const overrideWithMeta: PermissionOverride = {
    ...override,
    timestamp: Date.now(),
  };

  // Always set on window for immediate effect
  (window as any).__HOTKEYS_PERMISSION_OVERRIDE__ = overrideWithMeta;

  // Store in appropriate storage
  const storage = persist ? localStorage : sessionStorage;
  storage.setItem(PERMISSION_OVERRIDE_KEY, JSON.stringify(overrideWithMeta));

  console.info(`[HotKeys Debug] Permission override applied (${persist ? "persistent" : "session"})`, overrideWithMeta);
}

/**
 * Clear all permission overrides.
 */
function clearPermissionOverrides(): void {
  if (typeof window === "undefined") return;

  delete (window as any).__HOTKEYS_PERMISSION_OVERRIDE__;
  sessionStorage.removeItem(PERMISSION_OVERRIDE_KEY);
  localStorage.removeItem(PERMISSION_OVERRIDE_KEY);

  console.info("[HotKeys Debug] Permission overrides cleared");
}

/**
 * Get current module's hotkey permissions (with overrides applied).
 */
function getPermissions() {
  if (typeof window === "undefined") {
    return { error: "Not in browser context" };
  }

  const metadata = (window as any).__CAPITAL_RX_MODULE_METADATA__;
  const overrides = getPermissionOverrides();

  if (!metadata) {
    return {
      moduleId: "unknown",
      hasMetadata: false,
      canRegister: overrides?.canRegister ?? false,
      canUseGlobalScope: overrides?.canUseGlobalScope ?? false,
      message: "No module metadata found. Module may not be initialized yet.",
      overrides: overrides || null,
    };
  }

  const permissions = metadata.permissions || [];
  const features = metadata.features?.hotkeys || {};

  // Base permissions from metadata
  const baseCanRegister = permissions.includes("hotkeys:register") || features.enabled === true;
  const baseCanUseGlobalScope = permissions.includes("hotkeys:global-scope") || features.scopes?.includes("global");
  const baseAllowedScopes = features.scopes || [metadata.id];
  const baseMaxRegistrations = features.maxRegistrations || 25;

  // Apply overrides if present
  const finalCanRegister = overrides?.canRegister ?? baseCanRegister;
  const finalCanUseGlobalScope = overrides?.canUseGlobalScope ?? baseCanUseGlobalScope;
  const finalAllowedScopes = overrides?.allowedScopes ?? baseAllowedScopes;
  const finalMaxRegistrations = overrides?.maxRegistrations ?? baseMaxRegistrations;

  return {
    moduleId: metadata.id,
    hasMetadata: true,
    permissions,
    features,
    canRegister: finalCanRegister,
    canUseGlobalScope: finalCanUseGlobalScope,
    allowedScopes: finalAllowedScopes,
    maxRegistrations: finalMaxRegistrations,
    strictMode: overrides?.strictMode ?? features.strictMode !== false,
    scopeConfig: overrides?.scopeConfig ?? features.scopeConfig,
    // Show override status
    hasOverrides: overrides !== null,
    overrides: overrides || null,
    // Show original values for comparison
    originalValues: overrides
      ? {
          canRegister: baseCanRegister,
          canUseGlobalScope: baseCanUseGlobalScope,
          allowedScopes: baseAllowedScopes,
          maxRegistrations: baseMaxRegistrations,
        }
      : null,
  };
}

/**
 * Get all registered hotkeys from manager.
 */
function listRegistered() {
  if (typeof window === "undefined") {
    return { error: "Not in browser context" };
  }

  const manager = (window as any).__HOTKEY_MANAGER__;
  if (!manager) {
    return {
      error: "No HotkeyManager found. HotKeys may be disabled or not initialized.",
    };
  }

  try {
    const hotkeys = manager.getAll?.() || manager.getHotkeys?.() || [];
    return {
      count: hotkeys.length,
      hotkeys: hotkeys.map((hk: any) => ({
        combo: `${hk.config?.modifiers?.join("+") || ""}${hk.config?.modifiers?.length ? "+" : ""}${hk.config?.key || ""}`,
        description: hk.config?.description || hk.description,
        scope: hk.config?.scope || hk.scope,
        priority: hk.config?.priority || hk.priority,
        enabled: hk.config?.enabled ?? hk.enabled,
      })),
    };
  } catch (error) {
    return {
      error: `Failed to list hotkeys: ${error}`,
    };
  }
}

/**
 * Get complete configuration from all layers.
 */
function getConfig() {
  const shellFeatures = (window as any).__SHELL_FEATURES__?.getFeatureConfig?.();

  return {
    layer1_environment: {
      HOTKEYS_FEATURE_ENABLED,
      NODE_ENV: getEnvVar("NODE_ENV"),
      VITE_ENABLE_HOTKEYS: getEnvVar("VITE_ENABLE_HOTKEYS"),
      VITE_ENVIRONMENT: getEnvVar("VITE_ENVIRONMENT"),
    },
    layer2_metadata: getPermissions(),
    layer3_shell: shellFeatures || { error: "Shell features not available" },
    localStorage: {
      hotkeyPrefs: localStorage.getItem("capitalrx-hotkeys-prefs"),
      featureEnabled: localStorage.getItem("feature.hotkeys.enabled"),
    },
    runtimeOverrides: {
      __ENABLE_HOTKEYS__: (window as any).__ENABLE_HOTKEYS__,
    },
  };
}

/**
 * Check if hotkeys are enabled (all layers).
 */
function isEnabled() {
  const config = getConfig();
  return {
    layer1_enabled: config.layer1_environment.HOTKEYS_FEATURE_ENABLED,
    layer2_hasPermission: config.layer2_metadata.canRegister,
    layer3_shellAllows: config.layer3_shell.shellEnabled ?? true,
    finalResult:
      config.layer1_environment.HOTKEYS_FEATURE_ENABLED && config.layer2_metadata.canRegister && (config.layer3_shell.shellEnabled ?? true),
  };
}

/**
 * Force enable hotkeys (sets runtime override and localStorage).
 */
function forceEnable() {
  if (typeof window === "undefined") {
    console.error("Not in browser context");
    return;
  }

  console.warn("[HotKeys Debug] Force enabling hotkeys...");

  // Set runtime override
  (window as any).__ENABLE_HOTKEYS__ = true;

  // Clear localStorage disable flag
  localStorage.removeItem("feature.hotkeys.enabled");

  console.warn("[HotKeys Debug] Reloading page to apply changes...");
  window.location.reload();
}

/**
 * Force disable hotkeys (sets localStorage flag).
 */
function forceDisable() {
  if (typeof window === "undefined") {
    console.error("Not in browser context");
    return;
  }

  console.warn("[HotKeys Debug] Force disabling hotkeys...");

  // Remove runtime override
  delete (window as any).__ENABLE_HOTKEYS__;

  // Set localStorage disable flag
  localStorage.setItem("feature.hotkeys.enabled", "false");

  console.warn("[HotKeys Debug] Reloading page to apply changes...");
  window.location.reload();
}

/**
 * Clear all overrides and reset to default configuration.
 */
function reset() {
  if (typeof window === "undefined") {
    console.error("Not in browser context");
    return;
  }

  console.warn("[HotKeys Debug] Resetting to default configuration...");

  // Clear runtime override
  delete (window as any).__ENABLE_HOTKEYS__;

  // Clear localStorage flags
  localStorage.removeItem("feature.hotkeys.enabled");

  console.warn("[HotKeys Debug] Reloading page...");
  window.location.reload();
}

/**
 * Print help text for debugging utilities.
 */
function help() {
  console.info(`
═══════════════════════════════════════════════════════════════
  HotKeys Debugging Utilities
═══════════════════════════════════════════════════════════════

Available Commands:
  __HOTKEYS_DEBUG__.help()          → Show this help text
  __HOTKEYS_DEBUG__.isEnabled()     → Check if hotkeys are enabled
  __HOTKEYS_DEBUG__.getPermissions() → View module permissions
  __HOTKEYS_DEBUG__.getConfig()     → View all layer configurations
  __HOTKEYS_DEBUG__.listRegistered() → Show registered hotkeys
  __HOTKEYS_DEBUG__.forceEnable()   → Force enable (reloads)
  __HOTKEYS_DEBUG__.forceDisable()  → Force disable (reloads)
  __HOTKEYS_DEBUG__.reset()         → Reset to defaults (reloads)

Permission Override Commands (NO RELOAD REQUIRED):
  __HOTKEYS_DEBUG__.permissions.override({...})  → Set permission overrides
  __HOTKEYS_DEBUG__.permissions.grantAll()       → Grant all permissions
  __HOTKEYS_DEBUG__.permissions.denyAll()        → Deny all permissions
  __HOTKEYS_DEBUG__.permissions.setScope(name, config) → Configure specific scope
  __HOTKEYS_DEBUG__.permissions.setMaxRegistrations(n) → Set max registrations
  __HOTKEYS_DEBUG__.permissions.setStrictMode(bool)   → Toggle strict mode
  __HOTKEYS_DEBUG__.permissions.reset()          → Clear all overrides
  __HOTKEYS_DEBUG__.permissions.persist()        → Save current to localStorage
  __HOTKEYS_DEBUG__.permissions.current()        → View current overrides

Shell Utilities (if available):
  __SHELL_FEATURES__.isHotkeysEnabledForModule(id)
  __SHELL_FEATURES__.allowModule(id)
  __SHELL_FEATURES__.blockModule(id)
  __SHELL_FEATURES__.getFeatureConfig()

Examples:
  // Check current status
  __HOTKEYS_DEBUG__.isEnabled()

  // View what hotkeys are registered
  __HOTKEYS_DEBUG__.listRegistered()

  // See all configuration layers
  __HOTKEYS_DEBUG__.getConfig()

  // Temporarily enable in production for testing
  __HOTKEYS_DEBUG__.forceEnable()

  // Override permissions without reloading (for QA testing)
  __HOTKEYS_DEBUG__.permissions.grantAll()

  // Test with limited permissions
  __HOTKEYS_DEBUG__.permissions.override({
    canRegister: true,
    canUseGlobalScope: false,
    maxRegistrations: 5,
    allowedScopes: ["documentation"],
    strictMode: false  // Warn instead of block
  })

  // Test scope-specific limits
  __HOTKEYS_DEBUG__.permissions.setScope("global", {
    enabled: true,
    maxRegistrations: 3
  })

  // Reset to original metadata permissions
  __HOTKEYS_DEBUG__.permissions.reset()

═══════════════════════════════════════════════════════════════
  `);
}

/**
 * Permission override utilities object.
 */
const permissionUtils = {
  /**
   * View current permission overrides.
   */
  current() {
    return getPermissionOverrides();
  },

  /**
   * Set custom permission overrides.
   * @param config Override configuration.
   * @param persist If true, persists across browser sessions.
   */
  override(config: PermissionOverride, persist = false) {
    setPermissionOverrides({ ...config, reason: "Manual override via debug console" }, persist);
    return getPermissions();
  },

  /**
   * Grant all permissions (useful for testing).
   */
  grantAll() {
    setPermissionOverrides({
      canRegister: true,
      canUseGlobalScope: true,
      allowedScopes: ["global", "documentation", "manual-adjudication", "pa", "shell"],
      maxRegistrations: 1000,
      strictMode: false,
      reason: "grantAll() - Testing mode",
    });
    console.info("[HotKeys Debug] ✓ All permissions granted. Hotkeys will work without restrictions.");
    return getPermissions();
  },

  /**
   * Deny all permissions (useful for testing disabled state).
   */
  denyAll() {
    setPermissionOverrides({
      canRegister: false,
      canUseGlobalScope: false,
      allowedScopes: [],
      maxRegistrations: 0,
      strictMode: true,
      reason: "denyAll() - Testing disabled state",
    });
    console.info("[HotKeys Debug] ✗ All permissions denied. Hotkeys will be blocked.");
    return getPermissions();
  },

  /**
   * Configure a specific scope.
   * @param scopeName The scope to configure.
   * @param config The scope configuration.
   */
  setScope(scopeName: string, config: { enabled?: boolean; maxRegistrations?: number }) {
    const current = getPermissionOverrides() || {};
    const scopeConfig = { ...current.scopeConfig, [scopeName]: config };
    setPermissionOverrides({
      ...current,
      scopeConfig,
      reason: `setScope(${scopeName}) - Testing scope config`,
    });
    console.info(`[HotKeys Debug] Scope "${scopeName}" configured:`, config);
    return getPermissions();
  },

  /**
   * Set maximum registrations limit.
   * @param max The maximum number of registrations.
   */
  setMaxRegistrations(max: number) {
    const current = getPermissionOverrides() || {};
    setPermissionOverrides({
      ...current,
      maxRegistrations: max,
      reason: `setMaxRegistrations(${max}) - Testing limit`,
    });
    console.info(`[HotKeys Debug] Max registrations set to ${max}`);
    return getPermissions();
  },

  /**
   * Toggle strict mode.
   * @param strict If true, invalid registrations are blocked. If false, they just warn.
   */
  setStrictMode(strict: boolean) {
    const current = getPermissionOverrides() || {};
    setPermissionOverrides({
      ...current,
      strictMode: strict,
      reason: `setStrictMode(${strict}) - Testing enforcement`,
    });
    console.info(`[HotKeys Debug] Strict mode ${strict ? "enabled" : "disabled"}`);
    return getPermissions();
  },

  /**
   * Clear all permission overrides and return to metadata defaults.
   */
  reset() {
    clearPermissionOverrides();
    console.info("[HotKeys Debug] Permission overrides cleared. Using metadata defaults.");
    return getPermissions();
  },

  /**
   * Persist current session overrides to localStorage.
   */
  persist() {
    const current = getPermissionOverrides();
    if (current) {
      localStorage.setItem(
        PERMISSION_OVERRIDE_KEY,
        JSON.stringify({
          ...current,
          reason: (current.reason || "") + " (persisted)",
        }),
      );
      console.info("[HotKeys Debug] Current overrides persisted to localStorage.");
    } else {
      console.warn("[HotKeys Debug] No overrides to persist.");
    }
  },
};

// Export individual permission override functions for direct imports
export { getPermissionOverrides, setPermissionOverrides, clearPermissionOverrides };

// Export debug utilities
export const HotkeysDebug = {
  isEnabled,
  getPermissions,
  getConfig,
  listRegistered,
  forceEnable,
  forceDisable,
  reset,
  help,
  // Permission override utilities
  permissions: permissionUtils,
  // Direct access to override functions (for programmatic use)
  getPermissionOverrides,
  setPermissionOverrides,
  clearPermissionOverrides,
};

// Export types for external use
export type { PermissionOverride };

// Expose to window in development or if explicitly enabled
if (isDevelopmentMode() || HOTKEYS_FEATURE_ENABLED) {
  if (typeof window !== "undefined") {
    (window as any).__HOTKEYS_DEBUG__ = HotkeysDebug;
    console.info("[HotKeys] Debug utilities loaded. Type __HOTKEYS_DEBUG__.help() for usage instructions.");
  }
}
